import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  CheckCircle2, ChevronRight, AlertTriangle, RefreshCw,
  Layers, Package, PackageCheck, Ruler, Tag, SkipForward,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card } from '@/components/ui'
import { apiClient } from '@/lib/api.ts'
import { cn } from '@/lib/utils.ts'

// ─── Types ──────────────────────────────────────────────────────────────────

interface SapTenant {
  id: string
  code: string
  name: string
}

interface Step1Result {
  synced: number
  renamed: number
  deleted: number
  reassigned: number
  errors: string[]
  skippedCleanup?: boolean
}

interface SyncTenantProgress {
  tenantCode: string
  processed: number
  created: number
  updated: number
  errors: number
  done: boolean
}

interface ArticleSyncStatus {
  running: boolean
  lastResult?: {
    results: Array<{ tenantCode: string; created: number; updated: number; conflicts: number; errors: string[]; totalErrors: number }>
    error?: string
  }
  processed?: number
  created?: number
  updated?: number
  errors?: number
  tenants?: SyncTenantProgress[]
}

interface PushMissingStatus {
  running: boolean
  pushed?: number
  failed?: number
  skipped?: number
  total?: number
  error?: string
  tenants?: Array<{ tenantCode: string; pushed: number; failed: number }>
  errors?: string[]
}

interface PriceGroup {
  id: string
  name: string
  isActive: boolean
  memberCount: number
}

interface PriceGroupPushResult {
  groupId: string
  groupName: string
  total: number
  success: number
  failed: number
  errors: string[]
}

// ─── Step phase union ────────────────────────────────────────────────────────

type SyncPhase =
  | { kind: 'idle' }
  | { kind: 'step1-running'; substep: 'sync' | 'cleanup' | 'compare'; tenantCode: string }
  | { kind: 'step1-done'; result: Step1Result }
  | { kind: 'step2-starting' }
  | { kind: 'step2-running'; processed: number; created: number; updated: number; errors: number; tenants: SyncTenantProgress[] }
  | { kind: 'step2-done'; created: number; updated: number; conflicts: number; errors: number; errorMessages: string[] }
  | { kind: 'step3-starting' }
  | { kind: 'step3-running'; pushed: number; failed: number; total?: number }
  | { kind: 'step3-done'; pushed: number; failed: number; skipped: number; errors: string[] }
  | { kind: 'step4-running' }
  | { kind: 'step4-done'; created: number; skipped: number; tenantErrors: string[]; unsupported?: boolean }
  | { kind: 'step5-running'; current: number; total: number; tenantCode: string }
  | { kind: 'step5-done'; synced: number; errors: string[]; unsupported?: boolean }
  | { kind: 'step6-running'; current: number; total: number; currentGroupName: string }
  | { kind: 'step6-done'; results: PriceGroupPushResult[] }
  | { kind: 'error'; step: number; message: string }

// ─── Helpers ─────────────────────────────────────────────────────────────────

function StepBadge({ n, active, done }: { n: number; active: boolean; done: boolean }) {
  if (done) return <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" />
  return (
    <div className={cn(
      'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0',
      active ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-500',
    )}>{n}</div>
  )
}

const COLOR_MAP = {
  green: 'text-green-700 bg-green-100',
  blue: 'text-blue-700 bg-blue-100',
  orange: 'text-orange-700 bg-orange-100',
  red: 'text-red-700 bg-red-100',
  purple: 'text-purple-700 bg-purple-100',
  gray: 'text-gray-700 bg-gray-100',
} as const

function StatBadge({ label, value, color }: { label: string; value: number; color: keyof typeof COLOR_MAP }) {
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', COLOR_MAP[color])}>
      <span className="font-bold">{value}</span> {label}
    </span>
  )
}

function ErrorToggle({ errors }: { errors: string[] }) {
  const [open, setOpen] = useState(false)
  if (!errors.length) return null
  return (
    <div className="mt-1.5">
      <button className="text-xs text-orange-600 underline" onClick={() => setOpen(v => !v)}>
        {errors.length} Fehler {open ? '▲' : '▼'}
      </button>
      {open && (
        <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
          {errors.map((e, i) => <li key={i} className="text-xs text-red-600 font-mono truncate">{e}</li>)}
        </ul>
      )}
    </div>
  )
}

// ─── StepCard ────────────────────────────────────────────────────────────────

function StepCard({
  n, icon, title, description, active, done,
  result, runningLabel, runningSeconds, runningExtra, errorMessage,
  onStart, onContinue, onRetry, onSkip,
}: {
  n: number
  icon: ReactNode
  title: string
  description: string
  active: boolean
  done: boolean
  result?: ReactNode
  runningLabel?: string | null
  runningSeconds?: number
  runningExtra?: ReactNode
  errorMessage?: string | null
  onStart?: () => void
  onContinue?: () => void
  onRetry?: () => void
  onSkip?: () => void
}) {
  const isRunning = !!runningLabel
  const isPending = !active && !done && !errorMessage

  return (
    <div className={cn(
      'rounded-lg border p-4 transition-colors',
      done ? 'border-green-200 bg-green-50/40'
      : errorMessage ? 'border-red-200 bg-red-50/40'
      : active ? 'border-brand-200 bg-brand-50/30'
      : 'border-gray-200 bg-gray-50/40',
    )}>
      <div className="flex items-start gap-3">
        <StepBadge n={n} active={active || !!errorMessage} done={done} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn('text-gray-400', active && 'text-brand-600', done && 'text-green-600')}>{icon}</span>
            <span className={cn('font-medium text-sm', isPending && 'text-gray-400')}>{title}</span>
          </div>
          <p className={cn('text-xs mt-0.5', isPending ? 'text-gray-400' : 'text-gray-500')}>{description}</p>

          {isRunning && (
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-3.5 h-3.5 text-brand-500 animate-spin shrink-0" />
                <span className="text-xs text-brand-600">
                  {runningLabel}
                  {runningSeconds != null && runningSeconds > 0 && (
                    <span className="text-gray-400 ml-1">({runningSeconds}s)</span>
                  )}
                </span>
              </div>
              {runningExtra}
            </div>
          )}

          {result && !isRunning && <div className="mt-2">{result}</div>}

          {errorMessage && (
            <div className="flex items-start gap-2 mt-2 p-2 bg-red-50 rounded border border-red-200">
              <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
              <span className="text-xs text-red-700">{errorMessage}</span>
            </div>
          )}
        </div>

        <div className="shrink-0 flex flex-col gap-1.5 items-end">
          {onStart && (
            <Button variant="primary" size="sm" onClick={onStart}>
              Sync starten
            </Button>
          )}
          {onContinue && (
            <Button variant="primary" size="sm" trailingIcon={<ChevronRight className="w-4 h-4" />} onClick={onContinue}>
              Weiter
            </Button>
          )}
          {onRetry && (
            <Button variant="secondary" size="sm" leadingIcon={<RefreshCw className="w-4 h-4" />} onClick={onRetry}>
              Wiederholen
            </Button>
          )}
          {onSkip && (
            <Button variant="ghost" size="sm" leadingIcon={<SkipForward className="w-3.5 h-3.5" />} onClick={onSkip}>
              Überspringen
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────

// ─── Sync-Mandanten Konfiguration ────────────────────────────────────────────

interface SapTenantForSync {
  id: string
  code: string
  name: string
}

function SyncTenantsCard() {
  const queryClient = useQueryClient()
  const [syncTenantIds, setSyncTenantIds] = useState<string[]>([])
  const [saved, setSaved] = useState(false)

  const { data: settings } = useQuery<{ syncTenantIds: string[] }>({
    queryKey: ['service-catalog-settings'],
    queryFn: () => apiClient.get('/service-catalog/settings').then((r) => r.data),
  })

  const { data: sapTenants = [] } = useQuery<SapTenantForSync[]>({
    queryKey: ['article-groups-sap-tenants'],
    queryFn: () => apiClient.get('/articles/groups/sap-tenants').then((r) => r.data),
    retry: false,
  })

  useEffect(() => {
    if (settings) setSyncTenantIds(settings.syncTenantIds ?? [])
  }, [settings])

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.put('/service-catalog/settings', { syncTenantIds }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-catalog-settings'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  function toggleTenant(id: string) {
    setSyncTenantIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
  }

  return (
    <Card>
      <Card.Header>
        <h3 className="text-base font-semibold text-gray-900">Sync-Mandanten für Artikel</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          SAP-Mandanten, an die Artikel-Pushes (Servicekatalog und zukünftige Artikel-Aktionen) gesendet werden.
          Gilt global für alle Artikel — nicht nur für den Servicekatalog.
        </p>
      </Card.Header>
      <Card.Body>
        {sapTenants.length === 0 ? (
          <p className="text-sm text-gray-400 italic">
            Keine SAP-Mandanten konfiguriert. SAP-Adapter unter Einstellungen → Integrationen anlegen.
          </p>
        ) : (
          <div className="space-y-2 mb-3">
            {sapTenants.map((t) => (
              <label key={t.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncTenantIds.includes(t.id)}
                  onChange={() => toggleTenant(t.id)}
                  className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-500 mr-1">{t.code}</span>
                  {t.name}
                </span>
              </label>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400 mb-3">
          Beim SAP-Push werden alle ausgewählten Mandanten gleichzeitig beliefert. Fehler werden pro Mandant separat angezeigt.
        </p>
        <Button
          variant="primary"
          size="sm"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || sapTenants.length === 0}
        >
          {saved ? <><CheckCircle2 className="w-4 h-4 mr-1 text-green-200" /> Gespeichert</> : 'Speichern'}
        </Button>
      </Card.Body>
    </Card>
  )
}

export function SapSyncSection() {
  const [phase, setPhase] = useState<SyncPhase>({ kind: 'idle' })

  // Saved results for display after step completes
  const [r1, setR1] = useState<Step1Result | null>(null)
  const [r2, setR2] = useState<{ created: number; updated: number; conflicts: number; errors: number } | null>(null)
  const [r3, setR3] = useState<{ pushed: number; failed: number; skipped: number; errors: string[] } | null>(null)
  const [r4, setR4] = useState<{ created: number; skipped: number; tenantErrors: string[]; unsupported?: boolean } | null>(null)
  const [r5, setR5] = useState<{ synced: number; errors: string[]; unsupported?: boolean } | null>(null)
  const [r6, setR6] = useState<PriceGroupPushResult[] | null>(null)

  type ConflictResolveState =
    | { kind: 'running'; resolved: number; total: number }
    | { kind: 'done'; resolved: number }
    | { kind: 'error'; message: string }
  const [conflictResolveState, setConflictResolveState] = useState<ConflictResolveState | null>(null)

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const cleanupAbortRef = useRef<AbortController | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  // Watchdog for step 2: track last processed count + when it changed
  const step2LastProgressRef = useRef<{ processed: number; at: number }>({ processed: -1, at: Date.now() })
  const [step2StaleSeconds, setStep2StaleSeconds] = useState(0)
  // Watchdog for step 3
  const step3LastProgressRef = useRef<{ pushed: number; at: number }>({ pushed: -1, at: Date.now() })
  const [step3StaleSeconds, setStep3StaleSeconds] = useState(0)

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current)
    if (timerRef.current) clearInterval(timerRef.current)
    cleanupAbortRef.current?.abort()
  }, [])

  const currentStep = (() => {
    if (phase.kind === 'idle') return 0
    const m = phase.kind.match(/^step(\d+)/)
    if (m) return parseInt(m[1], 10)
    if (phase.kind === 'error') return (phase as { kind: 'error'; step: number }).step
    return 0
  })()

  const allDone = phase.kind === 'step6-done'

  function reset() {
    if (pollRef.current) clearInterval(pollRef.current)
    if (timerRef.current) clearInterval(timerRef.current)
    cleanupAbortRef.current?.abort()
    setPhase({ kind: 'idle' })
    setElapsedSeconds(0)
    setStep2StaleSeconds(0)
    setStep3StaleSeconds(0)
    step1SyncTotalsRef.current = { synced: 0, renamed: 0, errors: [] }
    step2LastProgressRef.current = { processed: -1, at: Date.now() }
    step3LastProgressRef.current = { pushed: -1, at: Date.now() }
    setR1(null); setR2(null); setR3(null); setR4(null); setR5(null); setR6(null)
    setConflictResolveState(null)
  }

  async function resolveAllConflictsAsLocal() {
    let ids: string[] = []
    try {
      const conflicts = await apiClient.get<Array<{ id: string }>>('/articles/conflicts').then(r => r.data)
      ids = conflicts.map((c: { id: string }) => c.id)
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? 'Unbekannter Fehler'
      setConflictResolveState({ kind: 'error', message: msg })
      return
    }
    if (ids.length === 0) {
      setConflictResolveState({ kind: 'done', resolved: 0 })
      return
    }
    setConflictResolveState({ kind: 'running', resolved: 0, total: ids.length })
    const BATCH_SIZE = 20
    let totalResolved = 0
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const batch = ids.slice(i, i + BATCH_SIZE)
      try {
        const result = await apiClient.post<{ resolved: number }>('/articles/conflicts/bulk-resolve', { ids: batch, resolution: 'local' }).then(r => r.data)
        totalResolved += result.resolved
        setConflictResolveState({ kind: 'running', resolved: totalResolved, total: ids.length })
      } catch (err: unknown) {
        const msg = (err as { message?: string })?.message ?? 'Unbekannter Fehler'
        setConflictResolveState({ kind: 'error', message: msg })
        return
      }
    }
    setConflictResolveState({ kind: 'done', resolved: totalResolved })
  }

  function startTimer() {
    if (timerRef.current) clearInterval(timerRef.current)
    setElapsedSeconds(0)
    timerRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000)
  }

  function stopTimer() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    setElapsedSeconds(0)
  }

  // Holds accumulated sync totals so skipCleanup can read them even before r1 is set
  const step1SyncTotalsRef = useRef({ synced: 0, renamed: 0, errors: [] as string[] })

  function skipCleanup() {
    cleanupAbortRef.current?.abort()
    stopTimer()
    const { synced, renamed, errors } = step1SyncTotalsRef.current
    const result: Step1Result = { synced, renamed, deleted: 0, reassigned: 0, errors, skippedCleanup: true }
    setR1(result)
    setPhase({ kind: 'step1-done', result })
  }

  // ─── Step 1: Warengruppen → SAP ──────────────────────────────────────────

  async function runStep1() {
    let tenants: SapTenant[] = []
    try {
      tenants = await apiClient.get('/articles/groups/sap-tenants').then(r => r.data)
    } catch {
      setPhase({ kind: 'error', step: 1, message: 'SAP-Mandanten konnten nicht geladen werden' })
      return
    }
    if (!tenants.length) {
      setPhase({ kind: 'error', step: 1, message: 'Kein SAP-Mandant konfiguriert' })
      return
    }

    let totalSynced = 0, totalRenamed = 0, totalDeleted = 0, totalReassigned = 0
    const allErrors: string[] = []
    step1SyncTotalsRef.current = { synced: 0, renamed: 0, errors: [] }

    const SYNC_BATCH_SIZE = 25

    for (const tenant of tenants) {
      try {
        // Sync step: paginated to keep each request under Passenger/Nginx timeout limit
        setPhase({ kind: 'step1-running', substep: 'sync', tenantCode: tenant.code })
        let syncOffset = 0
        let complete = false
        do {
          const syncRes = await apiClient.post('/articles/groups/sap-sync-step', { tenantId: tenant.id, step: 'sync', offset: syncOffset, limit: SYNC_BATCH_SIZE }, { timeout: 90000 }).then(r => r.data)
          totalSynced += syncRes.synced ?? 0
          totalRenamed += syncRes.renamed ?? 0
          allErrors.push(...(syncRes.errors ?? []).map((e: string) => `[${tenant.code}] ${e}`))
          complete = syncRes.complete ?? true
          syncOffset = syncRes.nextOffset ?? (syncOffset + SYNC_BATCH_SIZE)
        } while (!complete)
        // Update ref so skipCleanup has current totals
        step1SyncTotalsRef.current = { synced: totalSynced, renamed: totalRenamed, errors: [...allErrors] }

        // Cleanup: can be slow (many SAP calls). Use AbortController so user can skip.
        setPhase({ kind: 'step1-running', substep: 'cleanup', tenantCode: tenant.code })
        const controller = new AbortController()
        cleanupAbortRef.current = controller
        startTimer()
        try {
          const cleanupRes = await apiClient.post(
            '/articles/groups/sap-sync-step',
            { tenantId: tenant.id, step: 'cleanup' },
            { timeout: 300000, signal: controller.signal },
          ).then(r => r.data)
          totalDeleted += cleanupRes.deleted ?? 0
          totalReassigned += cleanupRes.reassigned ?? 0
          allErrors.push(...(cleanupRes.errors ?? []).map((e: string) => `[${tenant.code}] ${e}`))
        } catch (err) {
          // If aborted by user (skip button) - phase is already set to step1-done, just return
          if ((err as { name?: string }).name === 'CanceledError' || (err as { name?: string }).name === 'AbortError') return
          throw err
        } finally {
          stopTimer()
          cleanupAbortRef.current = null
        }

        setPhase({ kind: 'step1-running', substep: 'compare', tenantCode: tenant.code })
        await apiClient.post('/articles/groups/sap-sync-step', { tenantId: tenant.id, step: 'compare' }, { timeout: 60000 })
      } catch (err) {
        setPhase({ kind: 'error', step: 1, message: `[${tenant.code}] ${err instanceof Error ? err.message : String(err)}` })
        return
      }
    }

    const result: Step1Result = { synced: totalSynced, renamed: totalRenamed, deleted: totalDeleted, reassigned: totalReassigned, errors: allErrors }
    setR1(result)
    setPhase({ kind: 'step1-done', result })
  }

  function skipStep2() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    setStep2StaleSeconds(0)
    // Use whatever partial progress we have
    setPhase(prev => {
      if (prev.kind === 'step2-running') {
        const result = { created: prev.created, updated: prev.updated, conflicts: 0, errors: prev.errors }
        setR2(result)
        return { kind: 'step2-done', ...result, errorMessages: [] }
      }
      return { kind: 'step2-done', created: 0, updated: 0, conflicts: 0, errors: 0, errorMessages: [] }
    })
  }

  // ─── Step 2: Artikel ← SAP ───────────────────────────────────────────────

  async function runStep2() {
    setPhase({ kind: 'step2-starting' })
    step2LastProgressRef.current = { processed: -1, at: Date.now() }
    setStep2StaleSeconds(0)
    try {
      await apiClient.post('/articles/sync')
    } catch (err) {
      setPhase({ kind: 'error', step: 2, message: err instanceof Error ? err.message : String(err) })
      return
    }

    pollRef.current = setInterval(async () => {
      try {
        const status: ArticleSyncStatus = await apiClient.get('/articles/sync/status').then(r => r.data)
        if (status.running) {
          const processed = status.processed ?? 0
          const ref = step2LastProgressRef.current
          if (processed !== ref.processed) {
            step2LastProgressRef.current = { processed, at: Date.now() }
            setStep2StaleSeconds(0)
          } else {
            const stale = Math.floor((Date.now() - ref.at) / 1000)
            setStep2StaleSeconds(stale)
          }
          setPhase({ kind: 'step2-running', processed, created: status.created ?? 0, updated: status.updated ?? 0, errors: status.errors ?? 0, tenants: status.tenants ?? [] })
        } else {
          if (pollRef.current) clearInterval(pollRef.current)
          setStep2StaleSeconds(0)
          if (status.lastResult?.error) {
            setPhase({ kind: 'error', step: 2, message: status.lastResult.error })
            return
          }
          const totalCreated = status.lastResult?.results?.reduce((a, t) => a + t.created, 0) ?? (status.created ?? 0)
          const totalUpdated = status.lastResult?.results?.reduce((a, t) => a + t.updated, 0) ?? (status.updated ?? 0)
          const totalConflicts = status.lastResult?.results?.reduce((a, t) => a + t.conflicts, 0) ?? 0
          const totalErrors = status.lastResult?.results?.reduce((a, t) => a + t.totalErrors, 0) ?? (status.errors ?? 0)
          const errorMessages = status.lastResult?.results?.flatMap(t => t.errors) ?? []
          const result = { created: totalCreated, updated: totalUpdated, conflicts: totalConflicts, errors: totalErrors }
          setR2(result)
          setPhase({ kind: 'step2-done', ...result, errorMessages })
        }
      } catch {
        if (pollRef.current) clearInterval(pollRef.current)
        setPhase({ kind: 'error', step: 2, message: 'Verbindung zum Sync-Status unterbrochen' })
      }
    }, 2000)
  }

  function skipStep3() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    setStep3StaleSeconds(0)
    setPhase(prev => {
      if (prev.kind === 'step3-running') {
        const result = { pushed: prev.pushed, failed: prev.failed, skipped: 0, errors: [] }
        setR3(result)
        return { kind: 'step3-done', ...result }
      }
      return { kind: 'step3-done', pushed: 0, failed: 0, skipped: 0, errors: [] }
    })
  }

  // ─── Step 3: Artikel → SAP (fehlende pushen) ─────────────────────────────

  async function runStep3() {
    setPhase({ kind: 'step3-starting' })
    step3LastProgressRef.current = { pushed: -1, at: Date.now() }
    setStep3StaleSeconds(0)
    try {
      await apiClient.post('/articles/push-missing', {})
    } catch (err) {
      setPhase({ kind: 'error', step: 3, message: err instanceof Error ? err.message : String(err) })
      return
    }

    pollRef.current = setInterval(async () => {
      try {
        const status: PushMissingStatus = await apiClient.get('/articles/push-missing/status').then(r => r.data)
        if (status.running) {
          const pushed = status.pushed ?? 0
          const ref = step3LastProgressRef.current
          if (pushed !== ref.pushed) {
            step3LastProgressRef.current = { pushed, at: Date.now() }
            setStep3StaleSeconds(0)
          } else {
            setStep3StaleSeconds(Math.floor((Date.now() - ref.at) / 1000))
          }
          setPhase({ kind: 'step3-running', pushed, failed: status.failed ?? 0, total: status.total })
        } else {
          if (pollRef.current) clearInterval(pollRef.current)
          setStep3StaleSeconds(0)
          if (status.error) {
            setPhase({ kind: 'error', step: 3, message: status.error })
            return
          }
          const result = { pushed: status.pushed ?? 0, failed: status.failed ?? 0, skipped: status.skipped ?? 0, errors: status.errors ?? [] }
          setR3(result)
          setPhase({ kind: 'step3-done', ...result })
        }
      } catch {
        if (pollRef.current) clearInterval(pollRef.current)
        setPhase({ kind: 'error', step: 3, message: 'Verbindung zum Push-Status unterbrochen' })
      }
    }, 2000)
  }

  // ─── Step 4: Masseinheiten ← SAP ──────────────────────────────────────────

  async function runStep4() {
    setPhase({ kind: 'step4-running' })
    try {
      const res = await apiClient.post('/articles/units/import-from-all-sap', {}).then(r => r.data)
      const tenantErrors = (res.tenants ?? [])
        .filter((t: { error?: string; tenantCode: string }) => t.error)
        .map((t: { error: string; tenantCode: string }) => `[${t.tenantCode}] ${t.error}`)
      const allUnsupported = tenantErrors.length > 0 && tenantErrors.every((e: string) => e.includes('Unrecognized resource path'))
      const result = { created: res.created ?? 0, skipped: res.skipped ?? 0, tenantErrors: allUnsupported ? [] : tenantErrors, unsupported: allUnsupported }
      setR4(result)
      setPhase({ kind: 'step4-done', ...result })
      if (allUnsupported) { setTimeout(() => runStep5(), 300) }
    } catch (err) {
      setPhase({ kind: 'error', step: 4, message: err instanceof Error ? err.message : String(err) })
    }
  }

  // ─── Step 5: Masseinheiten → SAP ──────────────────────────────────────────

  async function runStep5() {
    let tenants: SapTenant[] = []
    try {
      tenants = await apiClient.get('/articles/groups/sap-tenants').then(r => r.data)
    } catch {
      setPhase({ kind: 'error', step: 5, message: 'SAP-Mandanten konnten nicht geladen werden' })
      return
    }

    let totalSynced = 0
    const allErrors: string[] = []

    for (let i = 0; i < tenants.length; i++) {
      const tenant = tenants[i]
      setPhase({ kind: 'step5-running', current: i + 1, total: tenants.length, tenantCode: tenant.code })
      try {
        const res = await apiClient.post('/articles/units/sync-to-sap', { tenantId: tenant.id, tenantCode: tenant.code }, { timeout: 60000 }).then(r => r.data)
        totalSynced += (res.created ?? 0) + (res.updated ?? 0)
        if (res.errors?.length) allErrors.push(...res.errors.map((e: string) => `[${tenant.code}] ${e}`))
      } catch (err) {
        allErrors.push(`[${tenant.code}] ${err instanceof Error ? err.message : String(err)}`)
      }
    }

    const allUnsupported = allErrors.length > 0 && allErrors.every(e => e.includes('Unrecognized resource path') || e.includes('status code 400'))
    const result = { synced: totalSynced, errors: allUnsupported ? [] : allErrors, unsupported: allUnsupported }
    setR5(result)
    setPhase({ kind: 'step5-done', ...result })
    if (allUnsupported) { setTimeout(() => runStep6(), 300) }
  }

  // ─── Step 6: Preisgruppen → SAP ──────────────────────────────────────────

  async function runStep6() {
    let groups: PriceGroup[] = []
    try {
      groups = await apiClient.get('/articles/price-groups').then(r => r.data)
    } catch (err) {
      setPhase({ kind: 'error', step: 6, message: err instanceof Error ? err.message : String(err) })
      return
    }

    const active = groups.filter(g => g.isActive && g.memberCount > 0)
    if (!active.length) {
      setR6([])
      setPhase({ kind: 'step6-done', results: [] })
      return
    }

    const results: PriceGroupPushResult[] = []
    for (let i = 0; i < active.length; i++) {
      const g = active[i]
      setPhase({ kind: 'step6-running', current: i + 1, total: active.length, currentGroupName: g.name })
      try {
        const res = await apiClient.post(`/articles/price-groups/${g.id}/push-to-sap`).then(r => r.data)
        results.push({
          groupId: g.id, groupName: g.name,
          total: res.total ?? 0,
          success: res.successArticles?.length ?? 0,
          failed: res.failedArticles?.length ?? 0,
          errors: res.failedArticles?.map((f: { itemCode: string; error: string }) => `${f.itemCode}: ${f.error}`) ?? [],
        })
      } catch (err) {
        results.push({ groupId: g.id, groupName: g.name, total: 0, success: 0, failed: 1, errors: [err instanceof Error ? err.message : String(err)] })
      }
    }

    setR6(results)
    setPhase({ kind: 'step6-done', results })
  }

  // ─── Render helpers ───────────────────────────────────────────────────────

  function getRunningLabel(): string | null {
    switch (phase.kind) {
      case 'step1-running':
        return `${phase.tenantCode}: ${{ sync: 'Gruppen synchronisieren…', cleanup: 'Verwaiste bereinigen…', compare: 'Abgleich…' }[phase.substep]}`
      case 'step2-starting': return 'Startet…'
      case 'step2-running': return `${phase.processed} Artikel verarbeitet (${phase.created} neu, ${phase.updated} aktualisiert)`
      case 'step3-starting': return 'Startet…'
      case 'step3-running': return phase.total ? `${phase.pushed}/${phase.total} gepusht, ${phase.failed} Fehler` : `${phase.pushed} gepusht…`
      case 'step4-running': return 'Einheiten aus SAP importieren…'
      case 'step5-running': return `${phase.current}/${phase.total}: ${phase.tenantCode}`
      case 'step6-running': return `${phase.current}/${phase.total}: ${phase.currentGroupName}`
      default: return null
    }
  }

  const label = getRunningLabel()

  return (
    <div className="space-y-4">
      <SyncTenantsCard />

      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Vollständiger SAP-Sync</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Warengruppen, Artikel, Einheiten und Preisgruppen in einem geführten Ablauf synchronisieren
              </p>
            </div>
            {(allDone || phase.kind === 'error') && (
              <Button variant="ghost" size="sm" leadingIcon={<RefreshCw className="w-4 h-4" />} onClick={reset}>
                Neu starten
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <div className="space-y-3">

            {/* Step 1 */}
            <StepCard n={1} icon={<Layers className="w-4 h-4" />}
              title="Warengruppen → SAP" description="Lokale Gruppen nach SAP pushen, verwaiste SAP-Gruppen bereinigen"
              active={currentStep === 1} done={currentStep > 1 && phase.kind !== 'error'}
              runningLabel={phase.kind.startsWith('step1') ? label : null}
              runningSeconds={phase.kind === 'step1-running' && phase.substep === 'cleanup' ? elapsedSeconds : undefined}
              errorMessage={phase.kind === 'error' && (phase as { step: number }).step === 1 ? phase.message : null}
              result={r1 ? (
                <div className="space-y-1.5">
                  <div className="flex flex-wrap gap-1.5">
                    <StatBadge label="Synchronisiert" value={r1.synced} color="green" />
                    <StatBadge label="Umbenannt" value={r1.renamed} color="blue" />
                    {!r1.skippedCleanup && <StatBadge label="Gelöscht" value={r1.deleted} color="orange" />}
                    {!r1.skippedCleanup && <StatBadge label="Verschoben" value={r1.reassigned} color="purple" />}
                  </div>
                  {r1.skippedCleanup && (
                    <p className="text-xs text-gray-400 italic">Bereinigung übersprungen</p>
                  )}
                  {!r1.skippedCleanup && <ErrorToggle errors={r1.errors} />}
                </div>
              ) : undefined}
              onStart={currentStep === 0 ? runStep1 : undefined}
              onContinue={phase.kind === 'step1-done' ? runStep2 : undefined}
              onRetry={phase.kind === 'error' && (phase as { step: number }).step === 1 ? runStep1 : undefined}
              onSkip={phase.kind === 'step1-running' && phase.substep === 'cleanup' && elapsedSeconds >= 15 ? skipCleanup : undefined}
            />

            {/* Step 2 */}
            <StepCard n={2} icon={<Package className="w-4 h-4" />}
              title="Artikel ← SAP" description="Alle Artikel, Beschreibungen und Preise aus SAP einlesen"
              active={currentStep === 2} done={currentStep > 2 && phase.kind !== 'error'}
              runningLabel={phase.kind.startsWith('step2') ? label : null}
              runningExtra={phase.kind === 'step2-running' && phase.tenants.length > 0 ? (
                <div className="ml-5 space-y-0.5">
                  {phase.tenants.map(t => (
                    <div key={t.tenantCode} className="flex items-center gap-2 text-xs">
                      {t.done
                        ? <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
                        : <RefreshCw className="w-3 h-3 text-brand-400 animate-spin shrink-0" />}
                      <span className="font-medium text-gray-600">{t.tenantCode}</span>
                      <span className="text-gray-400">
                        {t.processed} geladen
                        {t.created > 0 && `, ${t.created} neu`}
                        {t.updated > 0 && `, ${t.updated} aktualisiert`}
                        {t.errors > 0 && <span className="text-red-500">, {t.errors} Fehler</span>}
                      </span>
                    </div>
                  ))}
                  {step2StaleSeconds >= 15 && (
                    <p className="text-xs text-orange-500 mt-1">Kein Fortschritt seit {step2StaleSeconds}s</p>
                  )}
                </div>
              ) : undefined}
              errorMessage={phase.kind === 'error' && (phase as { step: number }).step === 2 ? phase.message : null}
              result={r2 ? (
                <div>
                  <div className="flex flex-wrap gap-1.5">
                    <StatBadge label="Neu" value={r2.created} color="green" />
                    <StatBadge label="Aktualisiert" value={r2.updated} color="blue" />
                    <StatBadge label="Konflikte" value={r2.conflicts} color="orange" />
                    <StatBadge label="Fehler" value={r2.errors} color="red" />
                    {phase.kind === 'step2-done' && <ErrorToggle errors={phase.errorMessages} />}
                  </div>
                  {r2.conflicts > 0 && conflictResolveState?.kind !== 'done' && (
                    <div className="mt-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={conflictResolveState?.kind === 'running'}
                        onClick={resolveAllConflictsAsLocal}
                      >
                        {conflictResolveState?.kind === 'running'
                          ? `${conflictResolveState.resolved}/${conflictResolveState.total} Konflikte aufgelöst…`
                          : `${r2.conflicts} Konflikte: SysMan-Werte nach SAP schreiben`}
                      </Button>
                      <p className="text-xs text-gray-500 mt-1">
                        SysMan-Wert gilt als maßgeblich. SAP-abweichende Werte werden unterdrückt und der SysMan-Wert wird in alle betroffenen SAP-Mandanten zurückgeschrieben.
                      </p>
                      {conflictResolveState?.kind === 'error' && (
                        <p className="text-xs text-red-600 mt-1">{conflictResolveState.message}</p>
                      )}
                    </div>
                  )}
                  {conflictResolveState?.kind === 'done' && (
                    <p className="text-xs text-green-600 mt-1.5">
                      {conflictResolveState.resolved} Konflikte aufgelöst – SysMan-Werte wurden nach SAP übertragen
                    </p>
                  )}
                </div>
              ) : undefined}
              onContinue={phase.kind === 'step2-done' ? runStep3 : undefined}
              onRetry={phase.kind === 'error' && (phase as { step: number }).step === 2 ? runStep2 : undefined}
              onSkip={phase.kind === 'step2-running' && step2StaleSeconds >= 30 ? skipStep2 : undefined}
            />

            {/* Step 3 */}
            <StepCard n={3} icon={<PackageCheck className="w-4 h-4" />}
              title="Artikel → SAP (fehlende)" description="Lokal vorhandene Artikel die in SAP noch fehlen, nach SAP pushen"
              active={currentStep === 3} done={currentStep > 3 && phase.kind !== 'error'}
              runningLabel={phase.kind.startsWith('step3') ? label : null}
              errorMessage={phase.kind === 'error' && (phase as { step: number }).step === 3 ? phase.message : null}
              result={r3 ? (
                <div>
                  <div className="flex flex-wrap gap-1.5">
                    <StatBadge label="Gepusht" value={r3.pushed} color="green" />
                    <StatBadge label="Übersprungen" value={r3.skipped} color="gray" />
                    {r3.failed > 0 && <StatBadge label="Fehler" value={r3.failed} color="red" />}
                  </div>
                  {r3.skipped > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Übersprungene Artikel sind bereits in allen SAP-Mandanten ihrer Warengruppe vorhanden – kein Push nötig.
                    </p>
                  )}
                  <ErrorToggle errors={r3.errors} />
                </div>
              ) : undefined}
              runningExtra={phase.kind === 'step3-running' && step3StaleSeconds >= 15 ? (
                <p className="text-xs text-orange-500 ml-5 mt-0.5">Kein Fortschritt seit {step3StaleSeconds}s</p>
              ) : undefined}
              onContinue={phase.kind === 'step3-done' ? runStep4 : undefined}
              onRetry={phase.kind === 'error' && (phase as { step: number }).step === 3 ? runStep3 : undefined}
              onSkip={phase.kind === 'step3-running' && step3StaleSeconds >= 30 ? skipStep3 : undefined}
            />

            {/* Step 4 */}
            <StepCard n={4} icon={<Ruler className="w-4 h-4" />}
              title="Maßeinheiten ← SAP" description="Einheiten aus allen SAP-Mandanten importieren"
              active={currentStep === 4} done={currentStep > 4 && phase.kind !== 'error'}
              runningLabel={phase.kind === 'step4-running' ? label : null}
              errorMessage={phase.kind === 'error' && (phase as { step: number }).step === 4 ? phase.message : null}
              result={r4 ? (
                r4.unsupported
                  ? <p className="text-xs text-gray-400 italic">Maßeinheiten-API nicht unterstützt – automatisch übersprungen</p>
                  : (
                    <div>
                      <div className="flex flex-wrap gap-1.5">
                        <StatBadge label="Importiert" value={r4.created} color="green" />
                        <StatBadge label="Bereits vorhanden" value={r4.skipped} color="gray" />
                      </div>
                      <ErrorToggle errors={r4.tenantErrors} />
                    </div>
                  )
              ) : undefined}
              onContinue={phase.kind === 'step4-done' ? runStep5 : undefined}
              onRetry={phase.kind === 'error' && (phase as { step: number }).step === 4 ? runStep4 : undefined}
            />

            {/* Step 5 */}
            <StepCard n={5} icon={<Ruler className="w-4 h-4" />}
              title="Maßeinheiten → SAP" description="Lokale Einheiten zu allen SAP-Mandanten synchronisieren"
              active={currentStep === 5} done={currentStep > 5 && phase.kind !== 'error'}
              runningLabel={phase.kind.startsWith('step5') ? label : null}
              errorMessage={phase.kind === 'error' && (phase as { step: number }).step === 5 ? phase.message : null}
              result={r5 ? (
                r5.unsupported
                  ? <p className="text-xs text-gray-400 italic">Maßeinheiten-API nicht unterstützt – automatisch übersprungen</p>
                  : (
                    <div>
                      <div className="flex flex-wrap gap-1.5">
                        <StatBadge label="Synchronisiert" value={r5.synced} color="green" />
                      </div>
                      <ErrorToggle errors={r5.errors} />
                    </div>
                  )
              ) : undefined}
              onContinue={phase.kind === 'step5-done' ? runStep6 : undefined}
              onRetry={phase.kind === 'error' && (phase as { step: number }).step === 5 ? runStep5 : undefined}
            />

            {/* Step 6 */}
            <StepCard n={6} icon={<Tag className="w-4 h-4" />}
              title="Preisgruppen → SAP" description="Alle aktiven Preisgruppen mit berechneten VK-Preisen nach SAP pushen"
              active={currentStep === 6} done={allDone}
              runningLabel={phase.kind === 'step6-running' ? label : null}
              errorMessage={phase.kind === 'error' && (phase as { step: number }).step === 6 ? phase.message : null}
              result={r6 ? <PriceGroupResult results={r6} /> : undefined}
              onRetry={phase.kind === 'error' && (phase as { step: number }).step === 6 ? runStep6 : undefined}
            />

          </div>
        </Card.Body>
      </Card>

      {allDone && (() => {
        const totalErrors =
          (r1?.errors.length ?? 0) +
          (r2?.errors ?? 0) +
          (r3?.failed ?? 0) +
          (r4?.tenantErrors.length ?? 0) +
          (r5?.errors.length ?? 0) +
          (r6?.reduce((a, r) => a + r.failed, 0) ?? 0)
        const hasErrors = totalErrors > 0
        return (
          <Card>
            <Card.Body>
              <div className="flex items-center gap-3">
                {hasErrors
                  ? <AlertTriangle className="w-6 h-6 text-orange-500 shrink-0" />
                  : <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" />}
                <div>
                  <p className="font-semibold text-gray-900">SAP-Sync abgeschlossen</p>
                  <p className="text-sm text-gray-500">
                    {hasErrors
                      ? `Alle sechs Schritte wurden durchgeführt – ${totalErrors} Fehler aufgetreten.`
                      : 'Alle sechs Schritte wurden erfolgreich durchgeführt.'}
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        )
      })()}
    </div>
  )
}

// ─── PriceGroupResult ────────────────────────────────────────────────────────

function PriceGroupResult({ results }: { results: PriceGroupPushResult[] }) {
  if (!results.length) {
    return <span className="text-xs text-gray-500">Keine aktiven Preisgruppen mit Mitgliedern gefunden</span>
  }
  const totalSuccess = results.reduce((a, r) => a + r.success, 0)
  const totalFailed = results.reduce((a, r) => a + r.failed, 0)
  const allErrors = results.flatMap(r => r.errors.map(e => `[${r.groupName}] ${e}`))

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        <StatBadge label="Gruppen" value={results.length} color="green" />
        <StatBadge label="Artikel gepusht" value={totalSuccess} color="blue" />
        {totalFailed > 0 && <StatBadge label="Fehler" value={totalFailed} color="red" />}
      </div>
      <ErrorToggle errors={allErrors} />
    </div>
  )
}
